// Library
import React from 'react';

// Supermove
import {Icon, Popover, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  PopoverType,
  ResponsiveType,
  useModal,
  usePopover,
  useResponsive,
  useEffect,
  useToast,
} from '@supermove/hooks';
import {AccountGrantModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ActionMenu from '@shared/design/components/ActionMenu';
import IconButton from '@shared/design/components/IconButton';
import Sheet from '@shared/design/components/Sheet';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import LoadingToast from '@shared/design/components/Toast/LoadingToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import DeactivateAccountGrantForm from '@shared/modules/AccountGrant/forms/DeactivateAccountGrantForm';
import useDeactivateAccountGrantMutation from '@shared/modules/AccountGrant/hooks/useDeactivateAccountGrantMutation';
import DeleteAccountGrantWarningModal from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/DeleteAccountGrantWarning';
import SetAsPrimaryAccountModal from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/SetAsPrimaryAccountModal';

interface AccountGrantActionsPopoverProps {
  accountGrant: AccountGrantModel;
  handleUpdateAccountGrant: (accountGrant: AccountGrantModel) => void;
  currentPrimaryAccount?: string;
}

const Container = Styled.View`
`;

const TooltipContainer = Styled.View`
`;

const PopoverBody = Styled.View<{responsive: ResponsiveType}>`
    width: ${({responsive}) => (responsive.desktop ? '200px' : '100%')};
    border-radius: 5px;
    border: ${({responsive}) => (responsive.desktop ? `1px solid ${colors.gray.border}` : '0px')};
    background-color: ${colors.white};
    overflow: visible;
    padding-vertical: 24px;
`;

const getSetPrimaryTooltip = (accountGrant: AccountGrantModel) => {
  if (accountGrant.isPrimary) {
    return 'This is already the primary account.';
  }
  if (accountGrant.status !== 'ACTIVE') {
    return 'This account is not active.';
  }
  return undefined;
};

const ActionMenuItem = ({
  label,
  onPress,
  isDisabled,
  tooltip,
  index,
  responsive,
  color,
}: {
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
  tooltip?: string | undefined;
  index: number;
  responsive: ResponsiveType;
  color?: string;
}) => {
  return (
    <ActionMenu.Item
      index={index}
      responsive={responsive}
      onPress={onPress}
      isDisabled={isDisabled}
      tooltip={tooltip}
      style={{paddingLeft: 24}}
      color={color}
    >
      {label}
    </ActionMenu.Item>
  );
};

const AccountGrantActionsPopoverOptions = ({
  handleUpdateAccountGrant,
  responsive,
  accountGrant,
  popover,
  handleSetAsPrimaryAccount,
  handleDisconnect,
}: {
  responsive: ResponsiveType;
  handleUpdateAccountGrant: () => void;
  accountGrant: AccountGrantModel;
  popover: PopoverType;
  handleSetAsPrimaryAccount: () => void;
  handleDisconnect: () => void;
}) => {
  const handleDisconnectAccount = () => {
    handleDisconnect();
    popover.handleClose();
  };

  const setPrimaryTooltip = getSetPrimaryTooltip(accountGrant);

  return (
    <PopoverBody responsive={responsive}>
      <ActionMenuItem
        index={0}
        responsive={responsive}
        onPress={handleSetAsPrimaryAccount}
        isDisabled={!!setPrimaryTooltip}
        tooltip={setPrimaryTooltip}
        label='Set as primary account'
      />
      {accountGrant.status !== 'ACTIVE' ? (
        <ActionMenuItem
          index={1}
          responsive={responsive}
          onPress={handleUpdateAccountGrant}
          label='Reconnect Account'
        />
      ) : null}
      <ActionMenuItem
        index={2}
        responsive={responsive}
        onPress={handleDisconnectAccount}
        label='Disconnect account'
        color={colors.red.warning}
      />
    </PopoverBody>
  );
};

const AccountGrantActionsPopoverContent = ({
  popover,
  handleUpdateAccountGrant,
  accountGrant,
  handleSetAsPrimaryAccount,
  responsive,
  handleDisconnect,
}: {
  popover: PopoverType;
  handleUpdateAccountGrant: () => void;
  accountGrant: AccountGrantModel;
  handleSetAsPrimaryAccount: () => void;
  responsive: ResponsiveType;
  handleDisconnect: () => void;
}) => {
  return responsive.desktop ? (
    <Popover
      key={popover.key}
      placement={Popover.Positions.BottomEnd}
      isOpen={popover.isOpen}
      handleOpen={popover.handleOpen}
      handleClose={popover.handleClose}
      reference={popover.ref}
      offset={[0, 4]}
    >
      <AccountGrantActionsPopoverOptions
        responsive={responsive}
        handleUpdateAccountGrant={handleUpdateAccountGrant}
        accountGrant={accountGrant}
        popover={popover}
        handleSetAsPrimaryAccount={handleSetAsPrimaryAccount}
        handleDisconnect={handleDisconnect}
      />
    </Popover>
  ) : (
    <Sheet.PreventPropagationContainer>
      <Sheet
        isOpen={popover.isOpen}
        handleClose={popover.handleClose}
        HeaderRightComponent={() => <Icon source={Icon.Xmark} />}
        headerText={'Actions'}
        isFixedHeight={false}
        headerContainerStyle={{backgroundColor: colors.white}}
      >
        <AccountGrantActionsPopoverOptions
          responsive={responsive}
          handleUpdateAccountGrant={handleUpdateAccountGrant}
          accountGrant={accountGrant}
          popover={popover}
          handleSetAsPrimaryAccount={handleSetAsPrimaryAccount}
          handleDisconnect={handleDisconnect}
        />
      </Sheet>
    </Sheet.PreventPropagationContainer>
  );
};

const AccountGrantActionsPopover = ({
  accountGrant,
  handleUpdateAccountGrant,
  currentPrimaryAccount,
}: AccountGrantActionsPopoverProps) => {
  const accountGrantOptionsPopover = usePopover({
    name: 'Account Grant Actions Popover',
  });
  const setAsPrimaryAccountModal = useModal({
    name: 'Set As Primary Account Modal',
  });
  const deleteAccountGrantWarningModal = useModal({
    name: 'Delete Account Grant Warning Modal',
  });
  const loadingToast = useToast({
    ToastComponent: LoadingToast,
    message: 'Disconnecting account.',
    duration: Infinity,
  });
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Account disconnected.',
  });
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: 'Error disconnecting account.',
  });
  const {handleSubmit: deactivateAccount, submitting: deactivatingAccount} =
    useDeactivateAccountGrantMutation({
      deactivateAccountGrantForm: DeactivateAccountGrantForm.edit({
        accountGrantId: accountGrant.id,
      }),
      onSuccess: () => {
        successToast.handleToast();
      },
      refetchQueries: [
        'OrganizationSettingsCommunicationAccountsTable',
        'CommunicationAccountsPage',
      ],
      onError: (errors) => {
        errorToast.handleToast();
        console.log({errors});
      },
    });

  useEffect(() => {
    if (deactivatingAccount) {
      loadingToast.handleToast();
    } else if (loadingToast.toastElementId) {
      loadingToast.dismissToast();
    }
  }, [deactivatingAccount]);
  const responsive = useResponsive();

  return (
    <Container>
      <AccountGrantActionsPopoverContent
        popover={accountGrantOptionsPopover}
        handleUpdateAccountGrant={() => handleUpdateAccountGrant(accountGrant)}
        accountGrant={accountGrant}
        handleSetAsPrimaryAccount={() => setAsPrimaryAccountModal.handleOpen()}
        responsive={responsive}
        handleDisconnect={() => deleteAccountGrantWarningModal.handleOpen()}
      />
      <Popover.Content innerRef={accountGrantOptionsPopover.ref}>
        <IconButton
          source={Icon.EllipsisV}
          onPress={accountGrantOptionsPopover.handleToggle}
          isSecondary={responsive.desktop}
        />
      </Popover.Content>
      <SetAsPrimaryAccountModal
        key={setAsPrimaryAccountModal.key}
        isOpen={setAsPrimaryAccountModal.isOpen}
        handleClose={setAsPrimaryAccountModal.handleClose}
        currentPrimaryAccount={currentPrimaryAccount}
        newPrimaryAccount={accountGrant}
      />
      <DeleteAccountGrantWarningModal
        isOpen={deleteAccountGrantWarningModal.isOpen}
        handleClose={deleteAccountGrantWarningModal.handleClose}
        accountGrant={accountGrant}
        hasOtherPrimaryAccount={
          !!currentPrimaryAccount && currentPrimaryAccount !== accountGrant.identifier
        }
        handleDelete={() => deactivateAccount()}
      />
    </Container>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
AccountGrantActionsPopover.fragment = gql`
  ${SetAsPrimaryAccountModal.fragment}
  ${DeleteAccountGrantWarningModal.fragment}

  fragment AccountGrantActionsPopover on AccountGrant {
    id
    isPrimary
    status
    identifier
    ...SetAsPrimaryAccountModal
    ...DeleteAccountGrantWarningModal
  }
`;

export default AccountGrantActionsPopover;
